import React from 'react';
import { CheckCircle, MapPin, Building, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const BabUpgradesPage = () => {
  const disciplines = [
    'Process Engineering',
    'Piping Engineering',
    'Mechanical Engineering',
    'Electrical Engineering',
    'Instrumentation & Control Engineering',
    'Telecom Engineering',
    'Civil & Structural Engineering',
    'Technical Safety (HSE) Engineering'
  ];

  const keyStats = [
    { value: '485,000', label: 'Barrels Per Day (bopd)' },
    { value: '118', label: 'New Wells' },
    { value: '462km', label: 'Flow Lines' },
    { value: '150km', label: 'Fiber Optic Cables' }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_precision-vision/artifacts/8r8oecmx_Bab%20Onshore.jpg)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            EPC For Bab Upgrades and Modifications Project
          </h1>
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">UAE</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              <span className="text-lg">ADNOC</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="text-lg">2025 - Till Now</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Project <span className="text-red-600">Overview</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              The Bab Upgrades and Modifications Project is a major ADNOC Onshore initiative designed to boost 
              production capacity at the UAE's largest onshore field to over 485,000 barrels per day (bopd) from 
              2023 onwards. It Involves 118 new wells (oil-producing, water injection, and artificial lift), 462km 
              of flow lines, and 150km of fiber optic cables.
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {keyStats.map((stat, index) => (
              <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold mb-2">{stat.value}</div>
                  <div className="text-red-100">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detailed Engineering Services */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Detailed Engineering Services
            </h3>
            <p className="text-slate-700 mb-6">
              Total Vision provides comprehensive engineering services across multiple disciplines for this flagship project:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {disciplines.map((discipline, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{discipline}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-6">
                Infrastructure Development
              </h3>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                This comprehensive project encompasses the development of 118 new wells, including oil-producing 
                wells, water injection wells for enhanced recovery, and artificial lift systems for production 
                optimization.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                The infrastructure includes 462 kilometers of new flow lines and 150 kilometers of fiber optic 
                cables, incorporating advanced monitoring and control systems for optimal operational efficiency.
              </p>
            </div>
            <Card className="border-none shadow-2xl">
              <CardContent className="p-8">
                <h4 className="text-xl font-bold text-slate-900 mb-4">Project Scope</h4>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Multidisciplinary detailed engineering services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Well development and optimization systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Extensive pipeline infrastructure network</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Advanced fiber optic communication systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Production capacity boost to 485,000 bopd</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Technical safety and HSE engineering integration</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Project Significance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Project Significance</h3>
            <p className="text-lg text-slate-700 leading-relaxed">
              As one of ADNOC Onshore's flagship initiatives, the Bab Upgrades and Modifications Project 
              represents a critical investment in the UAE's energy infrastructure. By significantly increasing 
              production capacity at the nation's largest onshore field, this project reinforces the UAE's 
              position as a leading global energy supplier while incorporating cutting-edge technology and 
              engineering excellence throughout its development.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BabUpgradesPage;
